import React, { Children, createElement } from 'react';
import { motion } from 'framer-motion';
import { LeafIcon, WindIcon, HammerIcon, CheckIcon } from 'lucide-react';
const services = [{
  title: 'Premium Lawn & Garden Care',
  description: "Keep your property pristine with our expert maintenance team. We provide comprehensive care tailored to your landscape's unique needs.",
  icon: LeafIcon,
  items: ['Professional Mowing & Edging', 'Tree Trimming & Pruning', 'Planting & Flower Beds', 'Professional Sod Laying', 'Fertilization & Mulching', 'Weeding & Weed Whacking', 'Blackberry Removal']
}, {
  title: 'Seasonal & Specialized Cleanup',
  description: 'Complete property restoration for any season.',
  icon: WindIcon,
  items: ['All-Season Cleanups', 'Leaf Blowing & Roof Blowing', 'Junk Removal & Debris Hauling', 'Pressure Washing']
}, {
  title: 'Hardscaping & Concrete',
  description: "Solid solutions for your property's infrastructure.",
  icon: HammerIcon,
  items: ['Professional Concrete Work', 'New Gravel Installation', 'Gravel Removal']
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    x: -10
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function Services() {
  return <section id="services" className="relative z-10 py-32 bg-tan overflow-hidden" data-id="element-214">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 opacity-5 pointer-events-none" data-id="element-215">
        <LeafIcon className="w-96 h-96 text-forest" data-id="element-216" />
      </div>
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 opacity-5 pointer-events-none rotate-180" data-id="element-217">
        <LeafIcon className="w-96 h-96 text-forest" data-id="element-218" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-219">
        <div className="text-center mb-20" data-id="element-220">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-221">
            <span className="text-gold font-bold tracking-widest uppercase text-sm mb-3 block" data-id="element-222">
              What We Do
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-forest mb-6 font-serif" data-id="element-223">
              Our Premium Services
            </h2>
            <div className="w-24 h-1.5 bg-gold mx-auto rounded-full mb-8" data-id="element-224"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light" data-id="element-225">
              Comprehensive landscaping solutions tailored to your property's
              unique needs, delivered with uncompromising quality.
            </p>
          </motion.div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-226">
          {/* Featured Service (Spans 8 cols on desktop) */}
          <motion.div variants={cardVariants} className="lg:col-span-7 group relative bg-forest rounded-3xl overflow-hidden shadow-2xl hover:shadow-[0_20px_50px_rgba(26,54,33,0.3)] transition-all duration-500" data-id="element-227">
            {/* Background Texture & Number */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#C2A55E_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-228"></div>
            <div className="absolute -right-10 -bottom-10 text-[15rem] font-serif font-bold text-forest-light/30 leading-none pointer-events-none group-hover:scale-110 transition-transform duration-700" data-id="element-229">
              01
            </div>

            <div className="relative z-10 p-10 md:p-14 h-full flex flex-col" data-id="element-230">
              <div className="w-20 h-20 bg-forest-light rounded-2xl flex items-center justify-center mb-10 group-hover:bg-gold transition-colors duration-500 shadow-lg" data-id="element-231">
                {createElement(services[0].icon, {
                className: 'w-10 h-10 text-gold group-hover:text-forest-dark transition-colors duration-500 group-hover:rotate-12'
              })}
              </div>

              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 font-serif" data-id="element-232">
                {services[0].title}
              </h3>
              <p className="text-gray-300 mb-10 text-lg md:text-xl font-light max-w-lg" data-id="element-233">
                {services[0].description}
              </p>

              <div className="mt-auto" data-id="element-234">
                <div className="w-full h-px bg-white/10 mb-8" data-id="element-235"></div>
                <motion.ul variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-236">
                  {services[0].items.map((item, i) => <motion.li key={i} variants={itemVariants} className="flex items-start gap-4" data-id="element-237">
                      <div className="mt-1 bg-white/5 p-1.5 rounded-full group-hover:bg-gold/20 transition-colors duration-300" data-id="element-238">
                        <CheckIcon className="w-4 h-4 text-gold" data-id="element-239" />
                      </div>
                      <span className="text-gray-200 font-medium" data-id="element-240">{item}</span>
                    </motion.li>)}
                </motion.ul>
              </div>
            </div>
          </motion.div>

          {/* Stacked Services (Spans 5 cols on desktop) */}
          <div className="lg:col-span-5 flex flex-col gap-8" data-id="element-241">
            {services.slice(1).map((service, index) => <motion.div key={service.title} variants={cardVariants} className="group relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 flex-1 border border-gray-100 hover:border-gold/30" data-id="element-242">
                {/* Background Texture & Number */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-tan-dark/30 rounded-bl-full -mr-10 -mt-10 transition-transform duration-500 group-hover:scale-150" data-id="element-243"></div>
                <div className="absolute right-6 top-6 text-6xl font-serif font-bold text-tan-dark/50 pointer-events-none group-hover:-translate-y-2 transition-transform duration-500" data-id="element-244">
                  0{index + 2}
                </div>

                <div className="relative z-10 p-8 md:p-10 h-full flex flex-col" data-id="element-245">
                  <div className="w-16 h-16 bg-tan rounded-2xl flex items-center justify-center mb-8 group-hover:bg-forest transition-colors duration-500" data-id="element-246">
                    <service.icon className="w-8 h-8 text-forest group-hover:text-gold transition-colors duration-500 group-hover:-rotate-12" data-id="element-247" />
                  </div>

                  <h3 className="text-2xl font-bold text-forest mb-4 font-serif pr-12" data-id="element-248">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-8 text-base font-light" data-id="element-249">
                    {service.description}
                  </p>

                  <div className="mt-auto" data-id="element-250">
                    <div className="w-full h-px bg-gray-100 mb-6" data-id="element-251"></div>
                    <motion.ul variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
                  once: true
                }} className="space-y-3" data-id="element-252">
                      {service.items.map((item, i) => <motion.li key={i} variants={itemVariants} className="flex items-start gap-3" data-id="element-253">
                          <div className="mt-0.5 bg-tan p-1 rounded-full group-hover:bg-gold/20 transition-colors duration-300" data-id="element-254">
                            <CheckIcon className="w-3.5 h-3.5 text-gold" data-id="element-255" />
                          </div>
                          <span className="text-gray-700 font-medium text-sm" data-id="element-256">
                            {item}
                          </span>
                        </motion.li>)}
                    </motion.ul>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}